<template>
  <div class="what-is-my-name-page">
    <div class="container">
      <!-- 面包屑导航 -->
      <BreadcrumbNav />
      
      <h1 class="page-title">What Is My Chinese Name? The Ultimate Identity Guide</h1>
      
      <div class="seo-intro">
        <div class="meta-info">
          <span>Updated: Jan 2026</span> • <span>Identity & Culture</span>
        </div>
        <p class="seo-description">
          Finding your Chinese name is a journey of self-discovery. 
          Learn the 3 authentic methods to translate your name: Phonetic, Semantic, and Cultural Customization.
        </p>
      </div>

      <div class="content">
        <!-- 1. 核心介绍卡片 -->
        <section class="guide-section intro-card">
          <div class="intro-wrapper">
            <div class="intro-text">
              <h2>The Soul of a Chinese Name</h2>
              <p>
                In Chinese culture, a name is not just a combination of sounds; it is a profound carrier of family heritage, parents' expectations, and cosmic balance. Whether you are a student of Mandarin, a business professional, or a cultural enthusiast, asking <strong>"What is my Chinese name?"</strong> is the first step toward a deeper connection with the Chinese-speaking world.
              </p>
              <p>
                Unlike Western names, which often prioritize phonetic tradition, Chinese names are <strong>ideographic</strong>. Each character carries a specific visual beauty, a historical story, and a philosophical meaning. In this guide, we explore the three most authentic methods to help you find an identity that truly belongs to you.
              </p>
            </div>
            <div class="intro-image">
              <img src="@/assets/images/all_name.png" alt="Finding your Chinese Identity" class="feature-img" onerror="this.src='https://placehold.co/600x400?text=Chinese+Identity'" />
            </div>
          </div>
        </section>

        <!-- 2. Method 1: Phonetic -->
        <section class="guide-section method-card method-1">
          <div class="method-header">
            <div class="method-icon">🗣️</div>
            <h2>Method 1: Phonetic Transliteration (Keeping the Sound)</h2>
          </div>
          
          <div class="method-content">
            <p>
              Transliteration is the most common approach for international travelers and beginners. It involves selecting Chinese characters that mimic the phonetic sounds of your original name.
            </p>
            <ul class="check-list">
              <li><strong>Recognition:</strong> It allows you to recognize your name easily in a conversation.</li>
              <li><strong>Social Ease:</strong> It provides a standard version of your name for official or social settings.</li>
            </ul>
            
            <div class="example-box">
              <h3>Common Transliteration Examples</h3>
              <p>Below is how some of the most popular English names are commonly written in Chinese:</p>
              <div class="name-grid">
                <div class="name-item">
                  <span class="eng">Michael</span>
                  <span class="arrow">→</span>
                  <span class="chn">迈克尔 (Mài-kè-ěr)</span>
                </div>
                <div class="name-item">
                  <span class="eng">Emma</span>
                  <span class="arrow">→</span>
                  <span class="chn">艾玛 (Ài-mǎ)</span>
                </div>
                <div class="name-item">
                  <span class="eng">Robert</span>
                  <span class="arrow">→</span>
                  <span class="chn">罗伯特 (Luó-bó-tè)</span>
                </div>
                <div class="name-item">
                  <span class="eng">Sophia</span>
                  <span class="arrow">→</span>
                  <span class="chn">苏菲亚 (Sū-fēi-yà)</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 3. Method 2: Semantic -->
        <section class="guide-section method-card method-2">
          <div class="method-header">
            <div class="method-icon">📖</div>
            <h2>Method 2: Semantic Translation (Capturing the Spirit)</h2>
          </div>
          
          <div class="method-content">
            <p>
              If your original name has a strong meaning (e.g., botanical, virtues, or historical roots), semantic translation is a more sophisticated way to create a Chinese identity.
            </p>
            <p>
              For example, if your name is <strong>Lily</strong>, you might choose the characters <strong>百合 (Bǎihé)</strong>, which literally means the flower Lily. This makes your name sound like a native name rather than a foreign translation.
            </p>
            
            <div class="tips-box blue">
              <h3>Why Choose Meaning Over Sound?</h3>
              <ul>
                <li><strong>Native Feel:</strong> It removes the "foreign" label from your name.</li>
                <li><strong>Conversation Starter:</strong> When you explain that your name means "Summer Rain" or "Brave Tiger," it creates a deeper impression.</li>
              </ul>
            </div>
          </div>
        </section>

        <!-- 4. Method 3: Cultural -->
        <section class="guide-section method-card method-3">
          <div class="method-header">
            <div class="method-icon">☯️</div>
            <h2>Method 3: Cultural & Zodiac Customization</h2>
          </div>
          
          <div class="method-content">
            <p>
              This is the traditional method used by Chinese parents. It involves analyzing your birth date (Ba Zi) and the Five Elements (Wu Xing).
            </p>
            
            <div class="elements-grid">
              <div class="element-card">
                <h3>⚖️ Balance</h3>
                <p>Choosing a name that compensates for a lack of Wood or Fire in your birth chart.</p>
              </div>
              <div class="element-card">
                <h3>🧧 Prosperity</h3>
                <p>Selecting characters with strokes that align with numerological harmony (Name Numerology).</p>
              </div>
            </div>
            
            <p class="method-footer">
              By integrating your zodiac sign (e.g., Year of the Dragon) with auspicious characters, you create a name that is believed to bring luck and smooth your life path.
            </p>
          </div>
        </section>

        <!-- 5. Taboos -->
        <section class="guide-section taboo-card">
          <h2>⚠️ Important: Chinese Naming Taboos to Avoid</h2>
          <div class="warning-box">
            <p>Choosing a Chinese name blindly can lead to embarrassing situations. Here are three things you must avoid:</p>
            <ul class="warning-list">
              <li><strong>Avoid Unlucky Homophones:</strong> Some characters sound like "death" (死), "sickness" (病), or "debt" (债). Always check the pronunciation with a native speaker.</li>
              <li><strong>Character Count:</strong> 99% of Chinese names are either 2 or 3 characters long. Avoid choosing a name with 4 or more characters unless you have a rare compound surname.</li>
              <li><strong>Don't Self-Promote Too Hard:</strong> Using titles like "King" (王) or "God" (神) within a given name can come across as arrogant or comedic.</li>
            </ul>
          </div>
        </section>

        <!-- 6. FAQ -->
        <section class="guide-section faq-card">
          <h2>💬 Frequently Asked Questions</h2>
          <div class="faq-list">
            <div class="faq-item">
              <h3>Do I need a Chinese surname?</h3>
              <p>Yes. A formal Chinese name always consists of a surname (usually 1 character) followed by a given name (1 or 2 characters). For example, in <strong>李小龙 (Bruce Lee)</strong>, <strong>李 (Li)</strong> is the surname.</p>
            </div>
            
            <div class="faq-item">
              <h3>Can I change my Chinese name later?</h3>
              <p>Technically, yes. However, since your Chinese name represents your social identity in the Sinophone world, it is better to choose a high-quality name from the start that grows with you.</p>
            </div>

            <div class="faq-item">
              <h3>How can I get a professional Chinese name?</h3>
              <p>You can use our <router-link to="/name-generator" class="link-text">AI Chinese Name Generator</router-link> which combines phonetic beauty with cultural depth to find the perfect match for you.</p>
            </div>
          </div>
        </section>

        <ExploreMore :currentPath="$route.path" />
      </div>
    </div>
  </div>
</template>

<script>
import BreadcrumbNav from '@/components/BreadcrumbNav.vue';
import ExploreMore from '@/components/ExploreMore.vue';

export default {
  name: 'WhatIsMyChineseName',
  components: {
    BreadcrumbNav,
    ExploreMore
  },
  head() {
    return {
      title: "What Is My Chinese Name? The Ultimate Identity Guide (2026)",
      meta: [
        { name: 'description', content: 'Discover your Chinese name using 3 authentic methods: Phonetic, Semantic, and Cultural. Learn how to translate English names to Chinese correctly.' },
        { name: 'keywords', content: 'what is my chinese name, translate name to chinese, english to chinese name, chinese name meanings' }
      ]
    };
  }
}
</script>

<style scoped>
/* =========== 页面全局布局 =========== */
.what-is-my-name-page {
  padding: 20px 0 100px;
  min-height: 100vh;
  background-color: #f8f9fa;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;
  box-sizing: border-box;
}

.page-title {
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 20px;
  margin-top: 50px;
  text-align: center;
  background: linear-gradient(135deg, #e60012 0%, #8a2be2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
}

.seo-intro {
  max-width: 900px;
  margin: 0 auto 3rem;
  text-align: center;
}

.meta-info {
  color: #888;
  font-size: 0.9rem;
  margin-bottom: 15px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.seo-description {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #555;
}

/* =========== 内容卡片化 =========== */
.content {
  display: flex;
  flex-direction: column;
  gap: 50px;
}

.guide-section {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  padding: 40px;
  border-top: 4px solid transparent;
}

/* 卡片边框色 */
.intro-card { border-top-color: #e60012; }
.method-1 { border-top-color: #3b82f6; }
.method-2 { border-top-color: #10b981; }
.method-3 { border-top-color: #8b5cf6; }
.taboo-card { border-top-color: #ef4444; }
.faq-card { border-top-color: #f59e0b; }

.guide-section h2 {
  font-size: 2rem;
  color: #333;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f0f0f0;
}

/* 1. Intro Section */
.intro-wrapper {
  display: flex;
  align-items: center;
  gap: 40px;
}

.intro-text { flex: 2; }
.intro-text p { font-size: 1.1rem; line-height: 1.8; color: #444; margin-bottom: 20px; }

.intro-image { flex: 1; text-align: center; }
.feature-img { max-width: 100%; border-radius: 12px; box-shadow: 0 8px 20px rgba(0,0,0,0.1); }

/* 2. Method Cards */
.method-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
}

.method-icon { font-size: 2.5rem; }

.method-content p {
  color: #555;
  line-height: 1.7;
  font-size: 1.05rem;
  margin-bottom: 15px;
}

.check-list { list-style: none; padding: 0; margin-bottom: 20px; }
.check-list li { margin-bottom: 10px; font-size: 1rem; color: #444; position: relative; padding-left: 20px; }
.check-list li::before { content: "✓"; color: #10b981; position: absolute; left: 0; font-weight: bold; }

.example-box {
  background: #f9fafb;
  padding: 25px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.example-box h3 { margin-top: 0; color: #333; margin-bottom: 10px; }

.name-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 15px;
}

.name-item {
  background: white;
  padding: 15px;
  border-radius: 8px;
  text-align: center;
  border: 1px solid #eee;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.eng { font-weight: bold; color: #333; }
.arrow { color: #999; margin: 5px 0; }
.chn { color: #e60012; font-weight: 500; }

/* Tips Box */
.tips-box {
  background: #eff6ff;
  border-left: 4px solid #3b82f6;
  padding: 20px;
  border-radius: 8px;
}
.tips-box h3 { margin-top: 0; color: #1e40af; }
.tips-box ul { padding-left: 20px; margin-bottom: 0; }
.tips-box li { margin-bottom: 8px; color: #444; }

/* Elements Grid (Method 3) */
.elements-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin: 25px 0;
}

.element-card {
  background: #fdf2f2;
  padding: 20px;
  border-radius: 10px;
  border: 1px solid #fecaca;
  text-align: center;
}

.element-card h3 { color: #b91c1c; margin-top: 0; }
.method-footer { font-style: italic; color: #666; margin-top: 15px; }

/* Taboos Warning */
.warning-box {
  background: #fffbeb;
  border: 1px solid #fcd34d;
  padding: 25px;
  border-radius: 12px;
}

.warning-list { padding-left: 20px; margin-top: 15px; }
.warning-list li { margin-bottom: 10px; color: #4b5563; }

/* FAQ */
.faq-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.faq-item {
  background: #f9fafb;
  padding: 20px;
  border-radius: 10px;
}

.faq-item h3 { margin-top: 0; color: #333; font-size: 1.2rem; }
.faq-item p { margin-bottom: 0; color: #555; }

.link-text {
  color: #e60012;
  font-weight: bold;
  text-decoration: underline;
}

/* 响应式 */
@media (max-width: 900px) {
  .intro-wrapper { flex-direction: column; }
  .elements-grid { grid-template-columns: 1fr; }
}

@media (max-width: 768px) {
  .page-title { font-size: 1.8rem; margin-top: 30px; }
  .guide-section { padding: 25px; }
}
</style>